import { Helmet } from 'react-helmet-async';
import Calculator from './Calculator';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

/**
 * Shared template for all language landing pages.
 *
 * Render order:
 *   1. <Helmet> (meta, canonical, hreflang, JSON-LD FAQPage)
 *   2. H1 + intro  (above the calculator)
 *   3. <Calculator>  (form + results + footer — footer is the last thing inside Calculator)
 *      └─ beforeFooter slot:
 *         4. sections  (text blocks)
 *         5. faqItems  (FAQ cards)
 *         6. moreLinks ("More Calculators") — rendered BEFORE Calculator footer
 */
export default function LandingPage({
  locale,
  helmet,           // { title, description, ogTitle, ogDescription, lang? }
  pageUrl,          // canonical + og:url
  hreflangList,     // [{ hreflang, href }]
  h1,
  intro,
  sections,         // [{ heading, body }]  body can be string or JSX
  faqItems,         // [{ q, a }]
  faqHeading,       // localised "FAQ" heading, e.g. "Preguntas Frecuentes"
  moreHeading,      // localised "More Calculators" label
  moreLinks,        // [{ href, label }]
  calculatorProps,  // extra props forwarded to <Calculator> (e.g. defaultMethod)
}) {
  const beforeFooter = (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
      {sections && sections.map(({ heading, body }, i) => (
        <div key={i}>
          <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
            {heading}
          </h2>
          {typeof body === 'string'
            ? <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>{body}</p>
            : <div style={{ marginBottom: 28 }}>{body}</div>
          }
        </div>
      ))}

      {faqItems && faqItems.length > 0 && (
        <>
          <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
            {faqHeading || 'Frequently Asked Questions'}
          </h2>
          {faqItems.map(({ q, a }) => (
            <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
              <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
              <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
            </div>
          ))}
        </>
      )}

      {moreLinks && moreLinks.length > 0 && (
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${COLORS.border}` }}>
          <p style={{ color: COLORS.muted, fontSize: '0.8rem', marginBottom: 12, letterSpacing: '1px', textTransform: 'uppercase' }}>
            {moreHeading || 'More Calculators'}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {moreLinks.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                style={{ color: COLORS.accent, fontSize: '0.9rem', textDecoration: 'none', padding: '6px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6 }}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        {helmet.lang && <html lang={helmet.lang} />}
        <title>{helmet.title}</title>
        <meta name="description" content={helmet.description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={helmet.ogTitle || helmet.title} />
        <meta property="og:description" content={helmet.ogDescription || helmet.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        {hreflangList && hreflangList.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hreflang={hreflang} href={href} />
        ))}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": (faqItems || []).map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {h1}
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          {intro}
        </p>
      </div>

      <Calculator
        locale={locale}
        showHelmet={false}
        showHeader={false}
        beforeFooter={beforeFooter}
        {...(calculatorProps || {})}
      />
    </div>
  );
}
